import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { InputFieldParams } from './input-field';
import styled from 'styled-components';
import { useSiteConfigContext } from 'style-context';
import { ProductModuleDefinitionEmbeddedConfig, getColor } from 'site-config';
import { getFieldError, getNextComponentId, setFormFocusOrBlur } from './utils';
import { getSteppedFormLabelColor, SteppedFormButton } from './utils/stepped-form';
import { Spacing } from 'rootstrap/global-styles';
import { StyledRootSchemaComponent } from './styles/root-schema-component-style';
import { ValidationTypes, getValidationMessage } from 'rootstrap/components-old/root-schema-form/utils/validation';
import { Field } from '../new-field';
import { Colors } from 'rootstrap/global-styles/colors';
import { usePromise, usePromiseLazy } from 'shared/hooks/promise';
import PlacesAutocomplete, { geocodeByPlaceId } from 'react-places-autocomplete';
import {
  addressFromGooglePlace,
  genericAddressErrorMessage,
  getGooglePlacesDefaultValue,
  validateAddress,
} from './utils/google-places';
import { Svg } from 'rootstrap/components/svg';
import NewSpinner, { AnimationTypes, SpinnerSize } from 'rootstrap/components/spinner/new-spinner';
import PoweredByGoogle from '../../../../assets/powered_by_google_on_white.svg';
import SearchIcon from '../../../../assets/search-icon.svg';

import { Address } from 'policyholder/domain/policyholder';
import { ThemedAlert } from 'rootstrap/components/alerts/alert';
import { StyledInput } from './styled-input';

export const GooglePlacesField = ({
  label,
  name,
  defaultValue,
  isDisabled,
  form,
  prefillAction,
  prefillValue,
  helpText,
  disableActiveElement,
  hideDivider,
  displayProperties,
  disableScrollToElement,
  validators,
  disableNextButton,
  isTouched: defaultIsTouched,
  submitOnChange,
  disableTitle,
  hideBorder,
  onPlaceSelected,
  isSuburbRequired,
  hiddenComponent,
}: InputFieldParams<string> & {
  isSuburbRequired: boolean;
  onPlaceSelected: (address: Address) => void;
}) => {
  const [inputValue, setInputValue] = React.useState<string>('');
  const [autoCompleteError, setAutoCompleteError] = React.useState<string | undefined>(undefined);
  const [isTouched, setTouched] = useState<boolean>(!!prefillValue || defaultIsTouched);
  const { siteConfig } = useSiteConfigContext();
  const [fieldError, setFieldError] = useState<string | undefined>(
    getFieldError({ errors: form?.errors, isTouched, name }),
  );

  const [isRequired] = useState<boolean>(
    !!validators?.find(({ validation }) => validation.type === ValidationTypes.REQUIRED),
  );
  const defaultFormControl = useForm().control;
  const { activeElement, setActiveElement } = displayProperties;
  const isActive = activeElement.elementId === name || activeElement.elementId === `stepped-form-next-button-${name}`;

  usePromise(async () => {
    if (defaultValue) {
      const textValue = await getGooglePlacesDefaultValue(defaultValue);
      setInputValue(textValue);
    }
  }, []);

  const errors = form?.errors;
  useEffect(() => {
    setFormFocusOrBlur({
      activeElement,
      scrollToId: name,
      disableScrollToElement,
      isFirstElement: displayProperties.index === 0,
      nextButtonFocus: false,
    });
  }, [JSON.stringify(errors), isTouched, name, isActive]);

  const onSelect = async (placeData: google.maps.GeocoderResult[]) => {
    try {
      const address = await addressFromGooglePlace(placeData[0]);
      validateAddress({ address, isSuburbRequired });
      onPlaceSelected(address);
      setAutoCompleteError(undefined);
    } catch (error: any) {
      if (error?.message === genericAddressErrorMessage) {
        setAutoCompleteError(error.message);
      } else {
        setAutoCompleteError('There was an error updating your address');
      }
    }
  };

  const onInputChange = (value: string) => {
    setAutoCompleteError(undefined);
    setInputValue(value);
    setAutoCompleteError(undefined);
  };

  const { execute, error, isLoading } = usePromiseLazy(async (value: string, googlePlaceId: string) => {
    const placeData = await geocodeByPlaceId(googlePlaceId);
    setInputValue(value);
    onSelect(placeData);
  }, []);

  useEffect(() => {
    error && setAutoCompleteError(error.message);
  }, [error]);

  const onAutoCompleteError = (status: string) => {
    if (status === 'ZERO_RESULTS') {
      setAutoCompleteError(`No Results found`);
    } else {
      setAutoCompleteError(`Google Maps API returned an error with status: ${status}`);
    }
  };

  return (
    <div id={name ? `${name}-form-group` : undefined}>
      <StyledRootSchemaComponent
        className='schema-component'
        isActive={isActive}
        disableActiveElement={disableActiveElement}
        isFirstElement={displayProperties.index === 0}
        hiddenComponent={hiddenComponent}
      >
        <Field
          isTouched={isTouched}
          hiddenComponent={hiddenComponent}
          disableTitle={disableTitle}
          isActive={isActive}
          name={name}
          onClick={() => setActiveElement({ elementId: name })}
          label={label}
          errors={fieldError ? [fieldError] : []}
          isRequired={isRequired}
          helpText={helpText}
          style={{
            color: getSteppedFormLabelColor({ isActive, siteConfig, color: Colors.Body }),
            fontSize: 16,
          }}
        >
          <Controller
            name={name}
            control={form?.control || defaultFormControl}
            defaultValue={defaultValue}
            rules={{
              required: isRequired ? 'Required' : undefined,
              validate: (value) =>
                getValidationMessage({
                  validators: validators,
                  value,
                  props: undefined,
                }),
            }}
            render={() => {
              return (
                <>
                  <PlacesAutocomplete
                    value={inputValue}
                    onChange={onInputChange}
                    onSelect={execute}
                    shouldFetchSuggestions={inputValue?.length > 3}
                    onError={onAutoCompleteError}
                  >
                    {({ getInputProps, suggestions, getSuggestionItemProps, loading: searching }) => (
                      <>
                        <StyledLogoAnchor>
                          <StyledLogo>
                            <Svg href={PoweredByGoogle} />
                          </StyledLogo>
                        </StyledLogoAnchor>

                        <StyledInput
                          icon={SearchIcon}
                          isTouched={isTouched}
                          siteConfig={siteConfig}
                          isActive={isActive}
                          name={name}
                          id={name}
                          {...getInputProps()}
                          fullWidth
                        />
                        {!autoCompleteError && (searching || isLoading || suggestions.length > 0) && (
                          <>
                            {isLoading || searching ? (
                              <SuggestionContainerStyle>
                                <GooglePlacesSearching
                                  searching={searching}
                                  setAutoCompleteError={setAutoCompleteError}
                                />
                              </SuggestionContainerStyle>
                            ) : (
                              <>
                                <SuggestionContainerStyle>
                                  {suggestions.slice(0, 4).map((suggestion, i) => {
                                    return (
                                      <div {...getSuggestionItemProps(suggestion)}>
                                        <SuggestionStyle id={`google-places-suggestion-${i}`} siteConfig={siteConfig}>
                                          {suggestion.description}
                                        </SuggestionStyle>
                                      </div>
                                    );
                                  })}
                                </SuggestionContainerStyle>
                                {/* <div style={{ marginBottom: 100 }} /> */}
                              </>
                            )}
                          </>
                        )}
                        {autoCompleteError && (
                          <div style={{ marginTop: 10 }}>
                            <ThemedAlert id='places-autocomplete-error' body={autoCompleteError} />
                          </div>
                        )}
                      </>
                    )}
                  </PlacesAutocomplete>
                </>
              );
            }}
          />
        </Field>
        {!hiddenComponent && (
          <SteppedFormButton
            disableActiveElement={!!disableActiveElement}
            hideBorder={hideBorder}
            submitOnChange={submitOnChange}
            setTouched={(isTouched) => setTouched(isTouched)}
            hideDivider={hideDivider}
            nextComponentId={getNextComponentId({ ...displayProperties, key: name })}
            getValue={() => form?.watch(name)}
            parentName={name}
            isDisabled={!!fieldError}
            isActive={isActive}
            setFieldError={(fieldError) => setFieldError(fieldError)}
            setActiveElement={(elementId) => {
              setActiveElement({
                elementId,
              });
            }}
            disableNextButton={disableNextButton}
            validationFunction={getValidationMessage}
            validators={validators}
            validationProps={{
              prefix: undefined,
            }}
          />
        )}
      </StyledRootSchemaComponent>
    </div>
  );
};

export const SuggestionContainerStyle = styled.div`
  background-color: white;
  border: 1px solid #ced4da;
  position: absolute;
  left: 16px;
  right: 16px;
  z-index: 10;
`;

export const SuggestionStyle = styled.span<{ siteConfig: ProductModuleDefinitionEmbeddedConfig | null }>`
  display: inline-block;
  width: 100%;
  padding-bottom: ${Spacing.xs};
  padding-top: ${Spacing.xs};
  padding-left: ${Spacing.sm};
  cursor: pointer;
  :hover {
    background: ${({ siteConfig }) => siteConfig?.styles.colors.backgroundHighlight};
  }
`;

const StyledLogoAnchor = styled.div`
  position: relative;
  width: 100%;
`;

export const StyledLogo = styled.span`
  position: absolute;
  right: 0;
  top: -22px;
  padding-right: 5px;
`;

const GooglePlacesSearching = (props: { searching: boolean; setAutoCompleteError: (v: string) => void }) => {
  const { searching, setAutoCompleteError } = props;
  const { siteConfig } = useSiteConfigContext();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setHasError(true);
      setAutoCompleteError('Our location suggestions are temporarily unavailable. Please try again later.');
    }, 15000);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return hasError ? (
    <></>
  ) : (
    <SuggestionStyle siteConfig={siteConfig}>
      <NewSpinner
        animation={AnimationTypes.Border}
        size={SpinnerSize.sm}
        color={getColor({ siteConfig, color: 'primary' })}
      />
      <span style={{ paddingLeft: 5 }} className='search-text'>
        {searching ? 'Searching' : 'Updating'}
      </span>
    </SuggestionStyle>
  );
};
